import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  Package,
  Users,
  Scissors,
  TrendingUp,
  DollarSign,
  Clock,
  AlertCircle,
} from "lucide-react";

export default function AdminDashboard() {
  return (
    <div>
      <PageHeader
        title="Admin Dashboard"
        subtitle="System-wide overview and management"
        actions={
          <Button asChild>
            <Link to="/admin/analytics">View Analytics</Link>
          </Button>
        }
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Orders</p>
                <p className="text-3xl font-bold mt-1">342</p>
                <p className="text-xs text-green-600 mt-1">+12% from last week</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Package className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Customers</p>
                <p className="text-3xl font-bold mt-1">1,248</p>
                <p className="text-xs text-green-600 mt-1">+8% from last week</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Workshops</p>
                <p className="text-3xl font-bold mt-1">24</p>
                <p className="text-xs text-muted-foreground mt-1">18 active today</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center">
                <Scissors className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Revenue (Week)</p>
                <p className="text-3xl font-bold mt-1">₹1.2L</p>
                <p className="text-xs text-green-600 mt-1">+15% from last week</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Order Status Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { label: "Pending", count: 45, color: "yellow" },
                { label: "In Progress", count: 128, color: "blue" },
                { label: "Completed", count: 169, color: "green" },
              ].map((status) => (
                <Card key={status.label} className="border-none shadow-sm bg-muted/50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">{status.label}</p>
                    <p className="text-3xl font-bold mt-2">{status.count}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Avg. Turnaround Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">21.5 hours</p>
                    <p className="text-xs text-muted-foreground">Within target</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">On-time delivery</span>
                  <span className="font-semibold">94%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Customer satisfaction</span>
                  <span className="font-semibold">4.8/5</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Top Performing Workshops</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/workshops">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                {
                  name: "Rekha Tailors",
                  orders: 156,
                  rating: 4.9,
                  turnaround: "18 hrs",
                },
                {
                  name: "Suman Alterations",
                  orders: 142,
                  rating: 4.8,
                  turnaround: "19 hrs",
                },
                {
                  name: "Priya Stitching",
                  orders: 128,
                  rating: 4.7,
                  turnaround: "20 hrs",
                },
              ].map((workshop) => (
                <div
                  key={workshop.name}
                  className="flex items-center gap-4 p-4 rounded-lg border"
                >
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Scissors className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{workshop.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {workshop.orders} orders · ⭐ {workshop.rating} · Avg. {workshop.turnaround}
                    </p>
                  </div>
                  <Button variant="outline" size="sm">View</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Alerts</CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/orders">View All</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                {
                  type: "Delayed Order",
                  message: "ORD-1240 exceeding 24-hour window",
                  severity: "high",
                },
                {
                  type: "Workshop Capacity",
                  message: "Rekha Tailors at 90% capacity",
                  severity: "medium",
                },
                {
                  type: "Customer Complaint",
                  message: "ORD-1235 quality issue reported",
                  severity: "high",
                },
              ].map((alert, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-lg border border-destructive/20 bg-destructive/5"
                >
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{alert.type}</p>
                    <p className="text-sm text-muted-foreground">{alert.message}</p>
                  </div>
                  <Button variant="ghost" size="sm">Review</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recent Orders</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/admin/orders">View All Orders</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              {
                id: "ORD-1248",
                customer: "Priya Sharma",
                workshop: "Rekha Tailors",
                items: 3,
                status: "in_progress" as const,
                amount: "₹1,200",
              },
              {
                id: "ORD-1247",
                customer: "Anjali Reddy",
                workshop: "Suman Alterations",
                items: 2,
                status: "confirmed" as const,
                amount: "₹850",
              },
              {
                id: "ORD-1246",
                customer: "Meera Patel",
                workshop: "Priya Stitching",
                items: 1,
                status: "completed" as const,
                amount: "₹450",
              },
            ].map((order) => (
              <div
                key={order.id}
                className="flex items-center gap-4 p-4 rounded-lg border"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{order.id}</p>
                  <p className="text-sm text-muted-foreground">
                    {order.customer} → {order.workshop}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {order.items} item{order.items > 1 ? "s" : ""} · {order.amount}
                  </p>
                </div>
                <StatusBadge status={order.status} />
                <Button variant="outline" size="sm">View</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
